import { createMachine, assign } from 'xstate';
import { getLocationIdsFromProvidersData } from '../../map-search/lib/utils';
import * as states from './map-search-states';
import * as actions from './map-search-actions';
import {
  MAP_SNACKBAR_EXPIRY_KEY,
  MAP_SNACKBAR_TTL
} from '../../map-search/lib/constants';

// outline for the context of the machine. facilitates proper
// initialization/error handling on startup
const emptyContext = {
  deepLinkingParams: undefined,
  searchParams: undefined,
  providers: undefined,
  locations: undefined,
  selectedLocation: undefined,
  providersForDrawer: undefined,
  numPagesToLoadInDrawer: undefined,
  hasMoreProvidersToLoad: undefined,
  fetchProviders: () => {},
  fetchLocationsWithIds: () => {},
  fetchProvidersByLocation: () => {}
};

/**
 * Returns the machine JSON for map-search. Exported to facilitate testing
 * @param {object} initialContext the initial context for the machine
 * @returns object
 */
export const machineJSON = (initialContext = emptyContext) => {
  const {
    deepLinkingParams,
    searchParams,
    providers,
    locations,
    selectedLocation,
    providersForDrawer,
    fetchProviders,
    fetchLocationsWithIds,
    fetchProvidersByLocation
  } = initialContext;
  return {
    id: 'map-search',
    context: {
      deepLinkingParams: deepLinkingParams,
      searchParams: searchParams,
      providers: providers,
      locations: locations,
      selectedLocation: selectedLocation,
      providersForDrawer: providersForDrawer,
      numPagesToLoadInDrawer: 1,
      hasMoreProvidersToLoad: false,
      fetchProviders: fetchProviders,
      fetchLocationsWithIds: fetchLocationsWithIds,
      fetchProvidersByLocation: fetchProvidersByLocation
    },
    initial: states.INITIALIZING,

    states: {
      [states.INITIALIZING]: {
        initial: states.ROUTING,
        states: {
          [states.ROUTING]: {
            always: [
              {
                cond: 'hasProvidersAndLocationsInContext',
                target: states.DONE
              },
              {
                cond: 'hasSearchParamsAndProvidersInContext',
                target: states.FETCHING_LOCATIONS
              },
              {
                cond: 'hasSearchParamsInContext',
                target: states.FETCHING_PROVIDERS
              },
              {
                target: states.ERROR
              }
            ]
          },
          [states.FETCHING_PROVIDERS]: {
            invoke: {
              id: 'fetchProviders',
              src: 'fetchProviders',
              onDone: [
                {
                  cond: 'hasProviderResults',
                  actions: actions.SET_PROVIDERS,
                  target: states.FETCHING_LOCATIONS
                },
                {
                  target: `#${states.NO_RESULTS}`
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.FETCHING_LOCATIONS]: {
            invoke: {
              id: 'fetchLocations',
              src: 'fetchLocations',
              onDone: [
                {
                  cond: 'hasLocationResults',
                  actions: actions.SET_LOCATIONS,
                  target: states.DONE
                },
                {
                  target: `#${states.NO_RESULTS}`
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.DONE]: { type: 'final' },
          [states.NO_RESULTS]: { type: 'final' },
          [states.ERROR]: { type: 'final' }
        },
        onDone: [
          {
            cond: 'hasProvidersAndLocationsInContext',
            target: states.CHECKING_DEEPLINK
          },
          {
            target: states.ERROR
          }
        ]
      },
      [states.CHECKING_DEEPLINK]: {
        always: [
          {
            // if we have deeplink params, save them and route to open provider drawer
            cond: 'hasValidDeepLinkingParams',
            actions: actions.SET_CONTEXT_FROM_DEEPLINK,
            target: states.PROVIDER_DRAWER_OPEN
          },
          {
            // otherwise go to default idle state
            target: states.IDLE
          }
        ]
      },
      [states.IDLE]: {
        type: 'parallel',
        states: {
          [states.IDLE_MAP_SEARCH]: {
            on: {
              [actions.BACK_TO_LIST_VIEW]: {},
              [actions.CLICK_PIN]: {
                actions: actions.SET_SELECTED_LOCATION,
                target: `#${states.PROVIDER_DRAWER_OPEN}`
              },
              [actions.OPEN_SORT_AND_FILTER]: {
                target: `#${states.SORT_AND_FILTER_DRAWER_OPEN}`
              },
              [actions.UPDATE_SEARCH_PARAMS]: {
                actions: actions.UPDATE_SEARCH_PARAMS,
                target: `#${states.FETCHING_PROVIDERS_AND_LOCATIONS}`
              }
            }
          },
          [states.SNACKBAR]: {
            initial: states.INITIALIZING,
            states: {
              [states.INITIALIZING]: {
                always: [
                  { target: states.SHOW, cond: 'shouldShowSnackbar' },
                  { target: states.NO_SHOW }
                ]
              },
              [states.SHOW]: {
                on: {
                  [actions.BACK_TO_LIST_VIEW]: {},
                  [actions.CLICK_PIN]: {
                    actions: actions.SET_SNACKBAR_EXPIRATION,
                    target: states.NO_SHOW
                  },
                  [actions.OPEN_SORT_AND_FILTER]: {
                    actions: actions.SET_SNACKBAR_EXPIRATION,
                    target: states.NO_SHOW
                  },
                  [actions.CLOSE_SNACKBAR]: {
                    actions: actions.SET_SNACKBAR_EXPIRATION,
                    target: states.NO_SHOW
                  }
                }
              },
              [states.NO_SHOW]: {}
            }
          }
        }
      },
      [states.PROVIDER_DRAWER_OPEN]: {
        id: states.PROVIDER_DRAWER_OPEN,
        initial: states.FETCHING_PROVIDERS,
        onDone: { target: states.IDLE },
        states: {
          [states.FETCHING_PROVIDERS]: {
            // reset page number anytime we fetch a new set of providers
            entry: actions.RESET_PROVIDER_PAGE_NUM,
            invoke: {
              id: 'fetchProvidersByLocationId',
              src: 'fetchProvidersByLocationId',
              onDone: [
                {
                  cond: 'isSingleBookableLocation',
                  actions: actions.SET_PROVIDERS_FOR_DRAWER,
                  target: states.RENDER_SINGLE_LOCATION
                },
                {
                  cond: 'hasProviderResults',
                  actions: [
                    actions.SET_PROVIDERS_FOR_DRAWER,
                    actions.SET_HAS_MORE_PROVIDERS_TO_LOAD
                  ],
                  target: states.RENDER_PROVIDERS
                },
                {
                  target: states.NO_RESULTS
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.RENDER_PROVIDERS]: {
            on: {
              [actions.BACK_TO_LIST_VIEW]: {},
              [actions.CLOSE_PROVIDER_DRAWER]: {
                actions: actions.UNSET_SELECTED_LOCATION,
                target: states.CLOSED
              },
              [actions.CLICK_PIN]: {
                actions: actions.SET_SELECTED_LOCATION,
                target: states.FETCHING_PROVIDERS
              },
              [actions.UPDATE_SEARCH_PARAMS]: {
                actions: actions.UPDATE_SEARCH_PARAMS,
                target: states.FETCHING_PROVIDERS
              },
              [actions.TOGGLE_GLOBAL_AVAILABILITY_MODAL]: {
                // TODO do we need this? whole machine here... or maybe just more substates
              },
              [actions.TOGGLE_PROVIDER_AVAILABILITY_MODAL]: {
                // TODO do we need this? whole machine here... or maybe just more substates
              },
              [actions.OPEN_BOOKING_CTA_MODAL]: {
                // TODO do we need this? booking CTA is part of tile, not machine
                target: states.BOOKING_CTA_MODAL_OPEN
              },
              [actions.LOAD_MORE_PROVIDERS]: {
                actions: actions.INCREMENT_PROVIDER_PAGE_NUM,
                target: states.LOADING_MORE_PROVIDERS
              }
            }
          },
          [states.RENDER_SINGLE_LOCATION]: {
            on: {
              [actions.BACK_TO_LIST_VIEW]: {},
              [actions.CLICK_PIN]: {
                actions: actions.SET_SELECTED_LOCATION,
                target: states.FETCHING_PROVIDERS
              },
              [actions.CLOSE_PROVIDER_DRAWER]: {
                actions: actions.UNSET_SELECTED_LOCATION,
                target: states.CLOSED
              }
            }
          },
          [states.LOADING_MORE_PROVIDERS]: {
            invoke: {
              id: 'fetchProvidersByLocationId',
              src: 'fetchProvidersByLocationId',
              onDone: [
                {
                  cond: 'hasProviderResults',
                  actions: [
                    actions.LOAD_MORE_PROVIDERS_INTO_DRAWER,
                    actions.SET_HAS_MORE_PROVIDERS_TO_LOAD
                  ],
                  target: states.RENDER_PROVIDERS
                },
                {
                  target: states.NO_RESULTS
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.BOOKING_CTA_MODAL_OPEN]: {
            on: {
              [actions.CLOSE_BOOKING_CTA_MODAL]: {
                target: states.RENDER_PROVIDERS
              },
              [actions.CLICK_BOOK_BUTTON]: {}
            }
          },
          [states.NO_RESULTS]: {
            on: {
              [actions.CLOSE_PROVIDER_DRAWER]: {
                target: states.CLOSED
              }
            }
          },
          [states.ERROR]: {
            on: {
              [actions.CLOSE_PROVIDER_DRAWER]: {
                target: states.CLOSED
              }
            }
          },
          [states.CLOSED]: { type: 'final' }
        }
      },
      [states.SORT_AND_FILTER_DRAWER_OPEN]: {
        id: states.SORT_AND_FILTER_DRAWER_OPEN,
        initial: states.IDLE,
        onDone: { target: states.IDLE },
        states: {
          [states.IDLE]: {
            on: {
              [actions.CLOSE_SORT_AND_FILTER]: {
                target: states.CLOSED
              },
              [actions.CLEAR_SEARCH_FILTERS]: {
                actions: actions.CLEAR_SEARCH_FILTERS,
                target: states.FETCHING_PROVIDERS_AND_LOCATIONS
              },
              [actions.SELECT_SORT_OPTION]: {
                actions: actions.SELECT_SORT_OPTION,
                target: states.FETCHING_PROVIDERS_AND_LOCATIONS
              },
              [actions.UPDATE_SEARCH_PARAMS]: {
                actions: actions.UPDATE_SEARCH_PARAMS,
                target: states.FETCHING_PROVIDERS_AND_LOCATIONS
              },
              [actions.CLICK_PIN]: {
                actions: actions.SET_SELECTED_LOCATION,
                target: `#${states.PROVIDER_DRAWER_OPEN}`
              }
            }
          },
          [states.FETCHING_PROVIDERS_AND_LOCATIONS]: {
            invoke: {
              id: 'fetchProvidersAndLocations',
              src: 'fetchProvidersAndLocations',
              onDone: [
                {
                  cond: 'hasProviderAndLocationResults',
                  actions: actions.SET_PROVIDERS_AND_LOCATIONS,
                  target: states.IDLE
                },
                {
                  cond: 'hasProviderNoMlocData',
                  target: states.NO_MLOC_DATA
                },
                {
                  target: states.NO_RESULTS
                }
              ],
              onError: {
                target: states.ERROR
              }
            }
          },
          [states.NO_RESULTS]: {},
          [states.NO_MLOC_DATA]: {},
          [states.ERROR]: {},
          [states.CLOSED]: { type: 'final' }
        }
      },
      [states.FETCHING_PROVIDERS_AND_LOCATIONS]: {
        id: states.FETCHING_PROVIDERS_AND_LOCATIONS,
        invoke: {
          id: 'fetchProvidersAndLocations',
          src: 'fetchProvidersAndLocations',
          onDone: [
            {
              cond: 'hasProviderAndLocationResults',
              actions: actions.SET_PROVIDERS_AND_LOCATIONS,
              target: states.IDLE
            },
            {
              target: states.NO_RESULTS
            }
          ],
          onError: {
            target: states.ERROR
          }
        }
      },
      [states.NO_RESULTS]: {
        id: states.NO_RESULTS,
        on: {
          [actions.SEARCH_THIS_AREA]: {
            actions: actions.UPDATE_SEARCH_CENTER_AND_RADIUS,
            target: states.FETCHING_PROVIDERS_AND_LOCATIONS
          }
        }
      },
      [states.ERROR]: {}
    }
  };
};

const machineActions = {
  [actions.SET_PROVIDERS]: assign({
    providers: (context, event) => event.data
  }),
  [actions.SET_LOCATIONS]: assign({
    locations: (context, event) => event.data
  }),
  [actions.SET_PROVIDERS_AND_LOCATIONS]: assign({
    locations: (context, event) => event.data.locations,
    providers: (context, event) => event.data.providers
  }),
  [actions.SET_CONTEXT_FROM_DEEPLINK]: assign({
    selectedLocation: (context) =>
      context.locations._locations.find(
        (loc) => loc.id === context.deepLinkingParams.locationId.toUpperCase()
      )
  }),
  [actions.SET_SELECTED_LOCATION]: assign({
    selectedLocation: (context, event) => event.data
  }),
  [actions.UNSET_SELECTED_LOCATION]: assign({
    selectedLocation: () => undefined
  }),
  [actions.SET_PROVIDERS_FOR_DRAWER]: assign({
    providersForDrawer: (context, event) => event.data
  }),
  [actions.LOAD_MORE_PROVIDERS_INTO_DRAWER]: assign({
    providersForDrawer: (context, event) => {
      return {
        ...context.providersForDrawer,
        _result: [...context.providersForDrawer._result, ...event.data._result]
      };
    }
  }),
  [actions.INCREMENT_PROVIDER_PAGE_NUM]: assign({
    numPagesToLoadInDrawer: (context) => context.numPagesToLoadInDrawer + 1
  }),
  [actions.RESET_PROVIDER_PAGE_NUM]: assign({
    numPagesToLoadInDrawer: () => 1
  }),
  [actions.SET_HAS_MORE_PROVIDERS_TO_LOAD]: assign({
    hasMoreProvidersToLoad: (context, event) =>
      context.providersForDrawer._result.length <
      event.data._metadata.total_providers
  }),
  [actions.CLEAR_SEARCH_FILTERS]: assign({
    // TODO remove the filters from the searchParams
    searchParams: (context, event) => event.data
  }),
  [actions.SELECT_SORT_OPTION]: assign({
    // TODO update the search params with the selected sort option
    searchParams: (context, event) => event.data
  }),
  [actions.UPDATE_SEARCH_PARAMS]: assign({
    searchParams: (context, event) => {
      return {
        ...context.searchParams,
        ...event.data
      };
    }
  }),
  [actions.SET_SNACKBAR_EXPIRATION]: () => {
    // Set an expiration for showing the snackbar again in local storage
    try {
      window.localStorage.setItem(
        MAP_SNACKBAR_EXPIRY_KEY,
        Date.now() + MAP_SNACKBAR_TTL
      );
    } catch (e) {
      /* Do Nothing */
    }
  }
};

const guards = {
  hasProvidersAndLocationsInContext: (context) =>
    context.searchParams && context.providers && context.locations,

  hasSearchParamsAndProvidersInContext: (context) =>
    context.searchParams && context.providers,

  hasSearchParamsInContext: (context) => context.searchParams,

  // if we only have a single provider with type location, then we're in the
  // bookable location use case (urgent care)
  isSingleBookableLocation: (context, event) =>
    event.data &&
    event.data._result &&
    event.data._result.length === 1 &&
    event.data._result[0].provider.entity_type === 'location',

  hasProviderResults: (context, event) =>
    event.data && event.data._result && event.data._result.length > 0,

  hasProviderNoMlocData: (context, event) => {
    const getPrimaryLocationFacet = event.data.providers.facets.find(
      (loc) => loc.field === 'locations.primary_marketable_location_id'
    );
    return (
      event.data &&
      getPrimaryLocationFacet.terms &&
      !getPrimaryLocationFacet.terms.length
    );
  },

  hasLocationResults: (context, event) =>
    event.data && event.data._locations && event.data._locations.length > 0,

  hasProviderAndLocationResults: (context, event) => {
    const getPrimaryLocationFacet = event.data.providers.facets.find(
      (loc) => loc.field === 'locations.primary_marketable_location_id'
    );
    return (
      event.data &&
      getPrimaryLocationFacet.terms &&
      getPrimaryLocationFacet.terms.length &&
      event.data.providers?._result &&
      event.data.providers?._result.length > 0 &&
      event.data.locations?._locations
    );
  },

  // check if we have deep link params in context
  hasValidDeepLinkingParams: (context) => {
    const locationId =
      context.deepLinkingParams &&
      context.deepLinkingParams.locationId &&
      /^loc\d+$/i.test(context.deepLinkingParams.locationId) &&
      context.deepLinkingParams.locationId.toUpperCase();
    if (locationId) {
      // check if location exists in fetched api data
      return ((context.locations || {})._locations || []).some(
        (loc) => loc.id === locationId
      );
    }
    return false;
  },

  shouldShowSnackbar: () => {
    const now = new Date();
    let snackbarExpiry;

    try {
      snackbarExpiry = window.localStorage.getItem(MAP_SNACKBAR_EXPIRY_KEY);
    } catch (e) {
      /* Do Nothing */
    }

    if (!snackbarExpiry || now.getTime() > snackbarExpiry) {
      return true;
    }
  }
};

const services = {
  fetchProviders: async (context) => {
    const { fetchProviders, searchParams } = context;
    const providers = await fetchProviders(searchParams);
    return providers;
  },
  fetchLocations: async (context) => {
    const { fetchLocationsWithIds, providers } = context;
    const locationIds = getLocationIdsFromProvidersData(providers) || [];
    const locations = await fetchLocationsWithIds(locationIds);
    return locations;
  },
  fetchProvidersByLocationId: async (context) => {
    const {
      fetchProvidersByLocation,
      selectedLocation,
      searchParams,
      numPagesToLoadInDrawer
    } = context;

    const searchParamsForEvent = {
      ...searchParams,
      page: numPagesToLoadInDrawer
    };

    const providersForDrawer = await fetchProvidersByLocation(
      selectedLocation.id,
      searchParamsForEvent
    );
    return {
      ...providersForDrawer,
      _result: providersForDrawer._result.map((item) => {
        return {
          ...item,
          provider: {
            ...item.provider,
            has_provider_availability: !!item.availability?.slots?.length
          }
        };
      })
    };
  },
  fetchProvidersAndLocations: async (context) => {
    const { fetchProviders, fetchLocationsWithIds, searchParams } = context;
    const providers = await fetchProviders(searchParams);
    const locationIds = getLocationIdsFromProvidersData(providers) || [];
    const locations = await fetchLocationsWithIds(locationIds);

    // fetchProviders and fetchLocationsWithIds return null if they error
    if (providers === null || locations === null) {
      throw new Error('Error fetching providers and locations');
    }

    return {
      providers,
      locations
    };
  }
};

/**
 * Creates and returns the machine that orchestrates the map-search page
 * @param {object} initialParams the initial context of the machine
 * @returns xstate.Machine
 */
export const createMapSearchMachine = (initialParams) => {
  const initialContext = { ...emptyContext, ...initialParams };
  return createMachine(machineJSON(initialContext)).withConfig({
    actions: machineActions,
    guards: guards,
    services: services
  });
};
